import { useState, useEffect, useMemo } from 'react';
import { Search, Trash2, Edit2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ChevronsUpDown, LayoutList, Plus, Globe, Trash, Link, CheckCircle, AlertCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

interface Story {
  id: string;
  type: string;
  category: string;
  created_at: string;
  translated_title: string;
  original_title: string;
  original_language?: string;
  poster_vertical_url?: string;
  poster_horizontal_url?: string;
  status?: string;
  video_url?: string;
  audio_url?: string;
  subtitle_url?: string;
  channel_id?: number;
  author?: string;
}

type SortField = 'original_title' | 'type' | 'category' | 'status' | 'created_at' | 'author' | 'original_language';
type SortDir = 'asc' | 'desc';

export default function MoviesSeriesList() {
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwt_token');
      let allStories: Story[] = [];
      let cursor = null;
      let hasMore = true;

      while (hasMore) {
        const url = new URL(`${API_URL}/content/stories`);
        url.searchParams.append('app_target', 'tv');
        url.searchParams.append('limit', '100');
        if (cursor) url.searchParams.append('cursor', cursor);

        const res = await fetch(url.toString(), {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (res.ok) {
          const data = await res.json();
          const items = data.data || data;
          if (Array.isArray(items)) allStories = [...allStories, ...items];
          if (data.nextCursor) cursor = data.nextCursor;
          else hasMore = false;
        } else hasMore = false;
      }
      setStories(allStories);
    } catch (err) {
      console.error('Failed to fetch stories:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronsUpDown size={14} className="text-slate-300" />;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const deleteStory = async (id: string) => {
    if (!confirm('Are you sure you want to delete this content? This action cannot be undone.')) return;

    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${API_URL}/content/story/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchStories();
        setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
      } else {
        alert('Failed to delete content');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('An error occurred while deleting');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedIds.length} items? This action cannot be undone.`)) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('jwt_token');
      await Promise.all(selectedIds.map(id =>
        fetch(`${API_URL}/content/story/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ));
      setSelectedIds([]);
      await fetchStories();
    } catch (err) {
      console.error('Bulk delete error:', err);
      alert('An error occurred during bulk deletion');
    } finally {
      setLoading(false);
    }
  };

  const filteredStories = useMemo(() => {
    let list = stories.filter(story => {
      const searchLower = search.toLowerCase();
      const translatedTitle = story.translated_title || '';
      const originalTitle = story.original_title || '';

      const matchesSearch = translatedTitle.toLowerCase().includes(searchLower)
                         || originalTitle.toLowerCase().includes(searchLower)
                         || story.id.toLowerCase().includes(searchLower);
      const matchesType = typeFilter ? story.type === typeFilter : true;
      const matchesCategory = categoryFilter ? story.category === categoryFilter : true;
      const matchesLanguage = languageFilter ? story.original_language === languageFilter : true;
      return matchesSearch && matchesType && matchesCategory && matchesLanguage;
    });

    list.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;
      return String(aVal).localeCompare(String(bVal)) * dir;
    });

    return list;
  }, [stories, search, typeFilter, categoryFilter, languageFilter, sortField, sortDir]);

  const categories = useMemo(() => Array.from(new Set(stories.map(s => s.category).filter(Boolean))), [stories]);
  const languages = useMemo(() => Array.from(new Set(stories.map(s => s.original_language).filter(Boolean))), [stories]);
  const totalPages = Math.ceil(filteredStories.length / itemsPerPage) || 1;
  const currentStories = filteredStories.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleAll = () => {
    if (selectedIds.length === currentStories.length) setSelectedIds([]);
    else setSelectedIds(currentStories.map(s => s.id));
  };

  const toggleRow = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const StatusBadge = ({ status }: { status?: string }) => {
    if (status === 'published') return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"><Eye size={12} /> Published</span>;
    if (status === 'draft') return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200"><EyeOff size={12} /> Draft</span>;
    return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200"><EyeOff size={12} /> Archived</span>;
  };

  const MediaBadge = ({ story }: { story: Story }) => {
    const hasV = !!story.video_url;
    const hasA = !!story.audio_url;
    const hasS = !!story.subtitle_url;
    const total = (hasV ? 1 : 0) + (hasA ? 1 : 0) + (hasS ? 1 : 0);
    if (total === 3) return <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><CheckCircle size={12} /> Complete</span>;
    if (total > 0) return <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full"><AlertCircle size={12} /> Partial</span>;
    return <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full"><XCircle size={12} /> Missing</span>;
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-500"></div></div>;

  return (
    <div className="max-w-7xl mx-auto p-6 xl:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <LayoutList className="w-6 h-6 text-indigo-500" />
            Movies & Series
          </h1>
          <p className="text-sm text-slate-500 mt-1">{stories.length} items · {filteredStories.length} shown</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <button onClick={handleBulkDelete} className="px-4 py-2.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg text-sm font-medium hover:bg-rose-100 transition-colors flex items-center gap-2">
              <Trash size={16} /> Delete {selectedIds.length}
            </button>
          )}
          <a href="/movies/new"
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm text-sm font-medium">
            <Plus size={18} /> Add Movie / Series
          </a>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input type="text" placeholder="Search by title or ID..." value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm bg-white" />
          </div>
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Types</option>
            <option value="Single/Movie">Single/Movie</option>
            <option value="Series">Series</option>
          </select>
          <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={languageFilter} onChange={(e) => { setLanguageFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 rounded-lg border border-slate-300 bg-white text-sm outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="">All Languages</option>
            {languages.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="p-4 w-10">
                  <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    checked={currentStories.length > 0 && selectedIds.length === currentStories.length} onChange={toggleAll} />
                </th>
                <th className="p-4 text-left">
                  <button onClick={() => toggleSort('original_title')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Title <SortIcon field="original_title" />
                  </button>
                </th>
                <th className="p-4 text-left hidden md:table-cell">
                  <button onClick={() => toggleSort('type')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Type <SortIcon field="type" />
                  </button>
                </th>
                <th className="p-4 text-left hidden lg:table-cell">
                  <button onClick={() => toggleSort('category')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Category <SortIcon field="category" />
                  </button>
                </th>
                <th className="p-4 text-left hidden xl:table-cell">
                  <button onClick={() => toggleSort('original_language')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Language <SortIcon field="original_language" />
                  </button>
                </th>
                <th className="p-4 text-left">
                  <button onClick={() => toggleSort('status')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Status <SortIcon field="status" />
                  </button>
                </th>
                <th className="p-4 text-left hidden lg:table-cell">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Media</span>
                </th>
                <th className="p-4 text-left hidden 2xl:table-cell">
                  <button onClick={() => toggleSort('created_at')} className="flex items-center gap-1 text-xs font-semibold text-slate-600 uppercase tracking-wider hover:text-slate-900">
                    Added <SortIcon field="created_at" />
                  </button>
                </th>
                <th className="p-4 text-right w-32">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentStories.length === 0 ? (
                <tr>
                  <td colSpan={9} className="p-12 text-center">
                    <div className="flex flex-col items-center text-slate-400">
                      <LayoutList size={40} className="mb-3 opacity-50" />
                      <p className="font-medium text-slate-500">No content found</p>
                      <p className="text-sm mt-1">Try adjusting your filters or add a new movie/series.</p>
                    </div>
                  </td>
                </tr>
              ) : currentStories.map(story => (
                <tr key={story.id} className={`hover:bg-slate-50 transition-colors group ${selectedIds.includes(story.id) ? 'bg-indigo-50/50' : ''}`}>
                  <td className="p-4"><input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    checked={selectedIds.includes(story.id)} onChange={() => toggleRow(story.id)} /></td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {story.poster_vertical_url ? (
                        <img src={story.poster_vertical_url} alt="cover" className="w-10 h-14 object-cover rounded shadow-sm bg-slate-200 flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-14 bg-slate-200 rounded flex items-center justify-center text-slate-400 flex-shrink-0"><LayoutList size={16} /></div>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate max-w-[250px]">{story.original_title}</p>
                        <p className="text-xs text-slate-400 font-mono mt-0.5">{story.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${story.type === 'Series' ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                      {story.type === 'Series' ? 'Series' : 'Single'}
                    </span>
                  </td>
                  <td className="p-4 hidden lg:table-cell text-slate-600 text-xs">{story.category || '—'}</td>
                  <td className="p-4 hidden xl:table-cell">
                    {story.original_language && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500 border border-slate-200 uppercase">{story.original_language}</span>
                    )}
                  </td>
                  <td className="p-4"><StatusBadge status={story.status} /></td>
                  <td className="p-4 hidden lg:table-cell"><MediaBadge story={story} /></td>
                  <td className="p-4 hidden 2xl:table-cell text-slate-500 text-xs">
                    {story.created_at ? new Date(story.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <a href={`${API_URL}/content/story/${story.id}`} target="_blank" rel="noreferrer"
                        className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="View API JSON">
                        <Globe size={16} />
                      </a>
                      <button onClick={() => { navigator.clipboard.writeText(`${API_URL}/content/story/${story.id}`); alert('API Link copied!'); }}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Copy API Link">
                        <Link size={16} />
                      </button>
                      <a href={`/movies/${story.id}`}
                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit Content">
                        <Edit2 size={16} />
                      </a>
                      <button onClick={() => deleteStory(story.id)}
                        className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Delete Content">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex justify-between items-center text-xs text-slate-500">
          <span>{(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filteredStories.length)} of {filteredStories.length}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed bg-white">
              <ChevronLeft size={16} />
            </button>
            <span className="font-medium text-slate-700 px-1">Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed bg-white">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-4 z-40 animate-in slide-in-from-bottom-5">
          <span className="font-medium">{selectedIds.length} selected</span>
          <div className="w-px h-6 bg-slate-700"></div>
          <button onClick={handleBulkDelete} className="hover:text-red-400 flex items-center gap-2 transition-colors text-sm font-medium">
            <Trash size={16} /> Delete
          </button>
        </div>
      )}
    </div>
  );
}
